import { useEffect, useMemo, useState } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import { ALL_CURRENCIES } from '../../lib/currency';
import { CurrencyPicker } from './CurrencyPicker';
import { cn } from '../../lib/utils';

interface CurrencyAmountInputProps {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  /** Currency for the amount entered in the input */
  currencyCode?: string;
  onCurrencyCodeChange?: (code: string) => void;
  /** Currency shown in the conversion preview (defaults to conversion view preference) */
  previewCurrencyCode?: string;
  onPreviewCurrencyCodeChange?: (code: string) => void;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  min?: number;
  step?: string;
  className?: string;
  disabled?: boolean;
  /** Smaller label/control heights for dense dialogs */
  compact?: boolean;
}

export function CurrencyAmountInput({
  label,
  value,
  onChange,
  currencyCode: currencyCodeProp,
  onCurrencyCodeChange,
  previewCurrencyCode: previewProp,
  onPreviewCurrencyCodeChange,
  required,
  helperText,
  placeholder = '0.00',
  min = 0,
  step = '0.01',
  className,
  disabled,
  compact = false,
}: CurrencyAmountInputProps) {
  const {
    currency: portalCurrency,
    conversionViewCurrency,
    setConversionViewCurrency,
    convert,
    formatAmount,
    ratesLoading,
    ratesError,
    ratesReady,
    refreshRates,
  } = useCurrency();

  const [localInputCurrency, setLocalInputCurrency] = useState(portalCurrency);
  useEffect(() => {
    if (!currencyCodeProp) {
      setLocalInputCurrency(portalCurrency);
    }
  }, [portalCurrency, currencyCodeProp]);

  const inputCurrency = currencyCodeProp ?? localInputCurrency;
  const setInputCurrency = onCurrencyCodeChange ?? setLocalInputCurrency;
  const previewCurrency = previewProp ?? conversionViewCurrency;
  const setPreviewCurrency = onPreviewCurrencyCodeChange ?? setConversionViewCurrency;

  useEffect(() => {
    if (inputCurrency !== previewCurrency) return;
    const fallback =
      ALL_CURRENCIES.find((c) => c.code !== inputCurrency)?.code ?? 'USD';
    const next = portalCurrency !== inputCurrency ? portalCurrency : fallback;
    setPreviewCurrency(next);
  }, [inputCurrency, previewCurrency, portalCurrency, setPreviewCurrency]);

  const numericValue = useMemo(() => {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) ? n : 0;
  }, [value]);

  const converted = useMemo(() => {
    if (!numericValue) return null;
    return convert(numericValue, inputCurrency, previewCurrency);
  }, [numericValue, inputCurrency, previewCurrency, convert]);

  const showPreview = inputCurrency !== previewCurrency && numericValue > 0;
  const conversionReady = converted !== null && ratesReady && !ratesError;

  return (
    <div className={className}>
      <label
        className={cn(
          'block font-semibold text-[hsl(var(--foreground))]',
          compact ? 'text-[10px] uppercase tracking-wide mb-1' : 'text-sm mb-2'
        )}
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className={cn('flex flex-col sm:flex-row sm:items-stretch', compact ? 'gap-1.5' : 'gap-2')}>
        <div
          className={cn(
            'flex flex-1 min-w-0 items-stretch border rounded-lg bg-[hsl(var(--card))] overflow-visible',
            compact ? 'border' : 'border-2',
            'border-[hsl(var(--border))] focus-within:ring-2 focus-within:ring-[hsl(var(--primary))] focus-within:border-transparent',
          )}
        >
          <CurrencyPicker
            value={inputCurrency}
            onChange={setInputCurrency}
            disabled={disabled}
            compact={compact}
          />
          <input
            type="number"
            min={min}
            step={step}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={cn(
              'flex-1 min-w-0 bg-transparent text-[hsl(var(--foreground))] focus:outline-none',
              compact ? 'px-2 py-1.5 text-xs font-semibold' : 'px-3 py-2.5 font-bold'
            )}
            placeholder={placeholder}
          />
        </div>

        {showPreview && (
          <div
            className={cn(
              'flex items-center gap-1 shrink-0 rounded-lg border border-[hsl(var(--border))]',
              'bg-[hsl(var(--muted))]/30 text-[hsl(var(--foreground))]',
              compact ? 'px-2 py-1' : 'px-3 py-2'
            )}
            title="Live conversion (ECB rates, refreshed hourly)"
          >
            <CurrencyPicker
              value={previewCurrency}
              onChange={(code) => {
                setPreviewCurrency(code);
              }}
              compact
              align="right"
              disabled={disabled}
            />
            <span
              className={cn(
                'font-bold whitespace-nowrap tabular-nums',
                compact ? 'text-xs' : 'text-sm'
              )}
            >
              {ratesLoading
                ? '…'
                : conversionReady
                  ? formatAmount(converted!, previewCurrency)
                  : '—'}
            </span>
          </div>
        )}
      </div>

      {helperText && (
        <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{helperText}</p>
      )}
      {showPreview && numericValue > 0 && !conversionReady && !ratesLoading && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          {ratesError ? (
            <>
              {ratesError}.{' '}
              <button
                type="button"
                className="underline font-medium"
                onClick={() => refreshRates()}
              >
                Retry
              </button>
            </>
          ) : (
            'Loading exchange rates…'
          )}
        </p>
      )}
    </div>
  );
}
